import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Search, AlertTriangle, CheckCircle2, Users } from 'lucide-react';

import { MasterToolbar }       from '@/components/master/MasterToolbar';
import { MasterSearchModal }   from '@/components/master/MasterSearchModal';
import type { SearchColumn }   from '@/components/master/MasterSearchModal';
import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { ReportHeader }        from '@/components/master/report/ReportHeader';
import { ReportSection }       from '@/components/master/report/ReportSection';
import { ReportFooter }        from '@/components/master/report/ReportFooter';
import { ConfirmDialog }       from '@/components/ui/ConfirmDialog';
import { Badge }               from '@/components/ui/Badge';
import { Combobox }            from '@/components/ui/Combobox';
import { Spinner }             from '@/components/ui/Spinner';
import { AccountLookupField }  from '@/components/erp/AccountLookupField';

import { useAuth }              from '@/hooks/useAuth';
import { usePermission }        from '@/hooks/usePermission';
import { useMasterScreenState } from '@/hooks/useMasterScreenState';
import { ScreenModeBadge } from '@/components/master/ScreenModeBadge';
import { Permissions }          from '@/constants/permissions';
import { AppRoutes }            from '@/constants/routes';
import { customerApi }          from '@/api/customer.api';
import { currencyApi }          from '@/api/currency.api';
import type { CustomerDto, CustomerListDto } from '@/types/customer.types';
import { cn } from '@/utils/cn';

// ── Zod schema ─────────────────────────────────────────────────────────────────

const schema = z.object({
  code:               z.string().min(1, 'Code is required').max(20),
  name:               z.string().min(1, 'Name is required').max(200),
  shortName:          z.string().max(50).nullable().optional(),
  currencyId:         z.string().uuid().nullable().optional(),
  controlAccountId:   z.string().uuid().nullable().optional(),
  creditLimit:        z.coerce.number().min(0, 'Credit limit cannot be negative'),
  creditPeriod:       z.coerce.number().int().min(0, 'Credit period cannot be negative'),
  creditHoldStatus:   z.boolean(),
  statementRequired:  z.boolean(),
  ageAnalysisEnabled: z.boolean(),
  interestApplicable: z.boolean(),
  collectionCategory: z.string().max(50).nullable().optional(),
  taxRegNumber:       z.string().max(50).nullable().optional(),
  address1:           z.string().max(200).nullable().optional(),
  address2:           z.string().max(200).nullable().optional(),
  address3:           z.string().max(200).nullable().optional(),
  city:               z.string().max(100).nullable().optional(),
  stateProvince:      z.string().max(100).nullable().optional(),
  postalCode:         z.string().max(20).nullable().optional(),
  country:            z.string().max(100).nullable().optional(),
  contactPerson:      z.string().max(100).nullable().optional(),
  phone:              z.string().max(30).nullable().optional(),
  mobile:             z.string().max(30).nullable().optional(),
  email:              z.string().max(200).email('Invalid email').or(z.literal('')).nullable().optional(),
  website:            z.string().max(200).nullable().optional(),
  remarks:            z.string().max(500).nullable().optional(),
  isActive:           z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const EMPTY_FORM: FormValues = {
  code: '', name: '', shortName: null,
  currencyId: null,
  controlAccountId: null,
  creditLimit: 0, creditPeriod: 30,
  creditHoldStatus: false, statementRequired: true,
  ageAnalysisEnabled: true, interestApplicable: false,
  collectionCategory: null, taxRegNumber: null,
  address1: null, address2: null, address3: null,
  city: null, stateProvince: null, postalCode: null, country: null,
  contactPerson: null, phone: null, mobile: null, email: null,
  website: null, remarks: null, isActive: true,
};

const TABS = ['General', 'Financial', 'Address', 'Contact', 'Audit'] as const;
type Tab = (typeof TABS)[number];

const SEARCH_COLUMNS: SearchColumn<CustomerListDto>[] = [
  { key: 'code', label: 'Code', className: 'w-24 font-mono' },
  { key: 'name', label: 'Name' },
  { key: 'creditHoldStatus', label: 'Hold', className: 'w-16 text-center',
    render: r => r.creditHoldStatus
      ? <Badge variant="danger">Hold</Badge>
      : <span className="text-gray-400 text-xs">—</span> },
  { key: 'isActive', label: 'Status', className: 'w-20',
    render: r => <Badge variant={r.isActive ? 'success' : 'default'}>{r.isActive ? 'Active' : 'Inactive'}</Badge> },
];

function dtoToForm(dto: CustomerDto): FormValues {
  return {
    code: dto.code, name: dto.name, shortName: dto.shortName ?? null,
    currencyId: dto.currencyId ?? null,
    controlAccountId: dto.controlAccountId ?? null,
    creditLimit: dto.creditLimit, creditPeriod: dto.creditPeriod,
    creditHoldStatus: dto.creditHoldStatus, statementRequired: dto.statementRequired,
    ageAnalysisEnabled: dto.ageAnalysisEnabled, interestApplicable: dto.interestApplicable,
    collectionCategory: dto.collectionCategory ?? null, taxRegNumber: dto.taxRegNumber ?? null,
    address1: dto.address1 ?? null, address2: dto.address2 ?? null, address3: dto.address3 ?? null,
    city: dto.city ?? null, stateProvince: dto.stateProvince ?? null,
    postalCode: dto.postalCode ?? null, country: dto.country ?? null,
    contactPerson: dto.contactPerson ?? null, phone: dto.phone ?? null,
    mobile: dto.mobile ?? null, email: dto.email ?? null,
    website: dto.website ?? null, remarks: dto.remarks ?? null,
    isActive: dto.isActive,
  };
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const inputClass = cn(
  'w-full h-9 px-3 rounded-md border text-sm',
  'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
  'border-gray-300 dark:border-gray-600',
  'focus:outline-none focus:ring-2 focus:ring-brand-500',
  'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center gap-2 mb-3 pb-1.5 border-b border-gray-100 dark:border-gray-700">
    <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">{children}</h3>
  </div>
);

const ToggleSwitch = ({
  label, description, checked, onChange, disabled,
}: {
  label: string; description?: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean;
}) => (
  <label className={cn('flex items-start gap-3 py-2', disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')}>
    <div className="relative mt-0.5 flex-shrink-0">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        disabled={disabled} className="sr-only peer" />
      <div className="w-9 h-5 rounded-full bg-gray-200 dark:bg-gray-600 peer-checked:bg-brand-500 peer-disabled:opacity-50 transition-colors" />
      <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white dark:bg-slate-800 shadow transition-transform peer-checked:translate-x-4" />
    </div>
    <div>
      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{label}</span>
      {description && <p className="text-[10px] text-gray-400 mt-0.5">{description}</p>}
    </div>
  </label>
);

// ── Component ─────────────────────────────────────────────────────────────────

export const CustomerMasterPage = () => {
  const navigate = useNavigate();
  const { user }          = useAuth();
  const { hasPermission } = usePermission();

  const canView   = hasPermission(Permissions.MST_CUSTOMER_VIEW);
  const canCreate = hasPermission(Permissions.MST_CUSTOMER_CREATE);
  const canEdit   = hasPermission(Permissions.MST_CUSTOMER_EDIT);
  const canDelete = hasPermission(Permissions.MST_CUSTOMER_DELETE);
  const canPrint  = hasPermission(Permissions.MST_CUSTOMER_PRINT);

  const screen = useMasterScreenState({ canEdit, canDelete });
  const codeInputRef = useRef<HTMLInputElement>(null);

  const [record,         setRecord]       = useState<CustomerDto | null>(null);
  const [codeInput,      setCodeInput]    = useState('');
  const [activeTab,      setActiveTab]    = useState<Tab>('General');
  const [searchOpen,     setSearchOpen]   = useState(false);
  const [saving,         setSaving]       = useState(false);
  const [lookupBusy,     setLookupBusy]   = useState(false);
  const [flashMsg,       setFlashMsg]     = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [printOpen,      setPrintOpen]    = useState(false);

  const [pendingSave,       setPendingSave]       = useState<FormValues | null>(null);
  const [showSaveConfirm,   setShowSaveConfirm]   = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNewConfirm,    setShowNewConfirm]    = useState<string | null>(null);

  const [controlAcct, setControlAcct] = useState<{ id: string | null; code: string | null; name: string | null }>({
    id: null, code: null, name: null,
  });

  const [currencies, setCurrencies] = useState<{ id: string; code: string; name: string }[]>([]);

  useEffect(() => {
    currencyApi.getList({ pageSize: 100, isActive: true })
      .then(r => setCurrencies((r.data.data?.items ?? []).map((c: { id: string; code: string; name: string }) => ({ id: c.id, code: c.code, name: c.name }))))
      .catch(() => {});
  }, []);

  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_FORM,
  });

  const flash = useCallback((type: 'success' | 'error', text: string) => {
    setFlashMsg({ type, text });
    setTimeout(() => setFlashMsg(null), 5000);
  }, []);

  // ── Reset to blank initial state ───────────────────────────────────────────
  const resetToInitial = useCallback(() => {
    setRecord(null);
    setCodeInput('');
    setControlAcct({ id: null, code: null, name: null });
    reset(EMPTY_FORM);
    screen.setMode('initial');
    setActiveTab('General');
    setPendingSave(null);
    setTimeout(() => codeInputRef.current?.focus(), 50);
  }, [reset, screen]);

  // ── Load a fetched DTO into view mode ──────────────────────────────────────
  const loadRecord = useCallback((dto: CustomerDto) => {
    setRecord(dto);
    reset(dtoToForm(dto));
    setCodeInput(dto.code);
    setControlAcct({ id: dto.controlAccountId, code: dto.controlAccountCode, name: dto.controlAccountName });
    screen.setMode('view');
    setActiveTab('General');
  }, [reset, screen]);

  // ── Code lookup ────────────────────────────────────────────────────────────
  const handleLookup = useCallback(async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    if (!canView) { flash('error', 'You do not have permission to view customers.'); return; }
    setLookupBusy(true);
    try {
      const res = await customerApi.getByCode(code);
      const dto = res.data.data;
      if (dto) {
        loadRecord(dto);
      } else {
        if (!canCreate) {
          flash('error', `Customer '${code}' was not found.`);
        } else {
          setShowNewConfirm(code);
        }
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        if (!canCreate) {
          flash('error', `Customer '${code}' was not found.`);
        } else {
          setShowNewConfirm(code);
        }
      } else {
        flash('error', 'Lookup failed. Please try again.');
      }
    } finally {
      setLookupBusy(false);
    }
  }, [codeInput, canView, canCreate, flash, loadRecord]);

  const handleConfirmCreateNew = () => {
    const code = showNewConfirm;
    setShowNewConfirm(null);
    if (!code) return;
    reset({ ...EMPTY_FORM, code });
    setCodeInput(code);
    setControlAcct({ id: null, code: null, name: null });
    setRecord(null);
    screen.setMode('new');
    setActiveTab('General');
  };

  const handleSearchSelect = useCallback((item: CustomerListDto) => {
    setSearchOpen(false);
    setLookupBusy(true);
    customerApi.getById(item.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load customer.'))
      .finally(() => setLookupBusy(false));
  }, [flash, loadRecord]);

  // ── Toolbar actions ────────────────────────────────────────────────────────
  const handleRefresh  = () => resetToInitial();
  const handleEdit     = () => screen.setMode('edit');
  const handleSaveClick = () => { handleSubmit(v => { setPendingSave(v); setShowSaveConfirm(true); })(); };
  const handleExit     = () => navigate(AppRoutes.AR_CUSTOMERS);
  const handlePrint    = () => { if (record) setPrintOpen(true); };
  const handleDelete   = () => setShowDeleteConfirm(true);

  // ── Save ───────────────────────────────────────────────────────────────────
  const executeSave = async () => {
    if (!pendingSave) return;
    setShowSaveConfirm(false);
    setSaving(true);
    const n = (v: string | null | undefined) => v?.trim() || null;
    try {
      if (!record) {
        // CREATE
        await customerApi.create({
          code:               pendingSave.code,
          name:               pendingSave.name,
          shortName:          n(pendingSave.shortName),
          currencyId:         pendingSave.currencyId ?? null,
          controlAccountId:   controlAcct.id,
          creditLimit:        pendingSave.creditLimit,
          creditPeriod:       pendingSave.creditPeriod,
          creditHoldStatus:   pendingSave.creditHoldStatus,
          statementRequired:  pendingSave.statementRequired,
          ageAnalysisEnabled: pendingSave.ageAnalysisEnabled,
          interestApplicable: pendingSave.interestApplicable,
          collectionCategory: n(pendingSave.collectionCategory),
          taxRegNumber:       n(pendingSave.taxRegNumber),
          address1:           n(pendingSave.address1),
          address2:           n(pendingSave.address2),
          address3:           n(pendingSave.address3),
          city:               n(pendingSave.city),
          stateProvince:      n(pendingSave.stateProvince),
          postalCode:         n(pendingSave.postalCode),
          country:            n(pendingSave.country),
          contactPerson:      n(pendingSave.contactPerson),
          phone:              n(pendingSave.phone),
          mobile:             n(pendingSave.mobile),
          email:              n(pendingSave.email),
          website:            n(pendingSave.website),
          remarks:            n(pendingSave.remarks),
        });
        flash('success', `Customer ${pendingSave.code} created successfully.`);
        resetToInitial();
      } else {
        // UPDATE
        await customerApi.update(record.id, {
          name:               pendingSave.name,
          shortName:          n(pendingSave.shortName),
          currencyId:         pendingSave.currencyId ?? null,
          controlAccountId:   controlAcct.id,
          creditLimit:        pendingSave.creditLimit,
          creditPeriod:       pendingSave.creditPeriod,
          creditHoldStatus:   pendingSave.creditHoldStatus,
          statementRequired:  pendingSave.statementRequired,
          ageAnalysisEnabled: pendingSave.ageAnalysisEnabled,
          interestApplicable: pendingSave.interestApplicable,
          collectionCategory: n(pendingSave.collectionCategory),
          taxRegNumber:       n(pendingSave.taxRegNumber),
          address1:           n(pendingSave.address1),
          address2:           n(pendingSave.address2),
          address3:           n(pendingSave.address3),
          city:               n(pendingSave.city),
          stateProvince:      n(pendingSave.stateProvince),
          postalCode:         n(pendingSave.postalCode),
          country:            n(pendingSave.country),
          contactPerson:      n(pendingSave.contactPerson),
          phone:              n(pendingSave.phone),
          mobile:             n(pendingSave.mobile),
          email:              n(pendingSave.email),
          website:            n(pendingSave.website),
          remarks:            n(pendingSave.remarks),
          isActive:           pendingSave.isActive,
        });
        flash('success', `Customer ${record.code} updated successfully.`);
        resetToInitial();
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      flash('error', msg ?? 'An error occurred while saving.');
    } finally {
      setSaving(false);
      setPendingSave(null);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const executeDelete = async () => {
    if (!record) return;
    setShowDeleteConfirm(false);
    setSaving(true);
    try {
      await customerApi.remove(record.id);
      flash('success', `Customer ${record.code} deleted.`);
      resetToInitial();
    } catch {
      flash('error', 'Failed to delete customer.');
    } finally {
      setSaving(false);
    }
  };

  // ── Fetch for search modal ─────────────────────────────────────────────────
  const fetchCustomers = useCallback(async (search: string, page: number, pageSize: number) => {
    const res = await customerApi.getList({ search, pageNumber: page, pageSize });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  }, []);

  // Fields disabled in initial and view modes
  const fieldsDisabled = screen.isInitial || screen.isView;
  // Lookup bar disabled when actively editing / saving
  const lookupDisabled = screen.isNew || screen.isEdit || saving;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full min-h-0">

      {/* Page header */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <Users size={18} className="text-brand-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h1 className="text-sm font-bold text-gray-900 dark:text-gray-100">Customer Maintenance</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">Manage customers, credit settings, and contact details</p>
        </div>
        <ScreenModeBadge isNew={screen.isNew} isEdit={screen.isEdit} isView={screen.isView} />
      </div>

      {/* Toolbar */}
      <MasterToolbar
        {...screen.toolbar}
        saving={saving}
        loading={lookupBusy}
        printEnabled={canPrint && !!record}
        deleteEnabled={screen.toolbar.deleteEnabled && !!record}
        onSave={handleSaveClick}
        onEdit={handleEdit}
        onPrint={handlePrint}
        onDelete={handleDelete}
        onRefresh={handleRefresh}
        onExit={handleExit}
      />

      {/* Flash message */}
      {flashMsg && (
        <div className={cn(
          'flex items-center gap-2 px-5 py-2.5 text-sm',
          flashMsg.type === 'success'
            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-b border-emerald-200 dark:border-emerald-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-b border-red-200 dark:border-red-800',
        )}>
          {flashMsg.type === 'success'
            ? <CheckCircle2 size={15} className="flex-shrink-0" />
            : <AlertTriangle size={15} className="flex-shrink-0" />}
          {flashMsg.text}
        </div>
      )}

      {/* ── Lookup bar (always visible) ───────────────────────────────────── */}
      <div className="flex items-center gap-3 px-5 py-2 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-200 dark:border-gray-700 flex-wrap">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 flex-shrink-0">Customer Code</span>
        <input
          ref={codeInputRef}
          type="text"
          value={codeInput}
          onChange={e => setCodeInput(e.target.value.toUpperCase())}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleLookup(); } }}
          disabled={lookupDisabled}
          placeholder="Code + Enter…"
          maxLength={20}
          className={cn(
            'w-36 h-8 px-3 text-sm font-mono rounded-md border uppercase',
            'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
            'placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500',
            'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
            'border-gray-300 dark:border-gray-600',
          )}
        />
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          disabled={lookupDisabled}
          className={cn(
            'h-8 w-8 flex items-center justify-center rounded-md border',
            'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800',
            'text-gray-500 dark:text-gray-400 hover:text-brand-600 hover:border-brand-400',
            'disabled:opacity-40 disabled:cursor-not-allowed transition-colors',
          )}
          title="Browse customers"
        >
          {lookupBusy ? <Spinner size="sm" /> : <Search size={14} />}
        </button>

        {record && (
          <div className="flex items-center gap-2 ml-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{record.name}</span>
            {record.creditHoldStatus && (
              <span className="px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-[10px] font-bold">Credit Hold</span>
            )}
            {!record.isActive && (
              <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-[10px] font-bold">Inactive</span>
            )}
          </div>
        )}
      </div>

      {/* ── Main form (always visible) ────────────────────────────────────── */}
      <div className="flex-1 overflow-auto">
        <form onSubmit={e => e.preventDefault()} noValidate>

          {/* Header row — Code / Name / Status */}
          <div className="px-5 pt-4 pb-3 grid grid-cols-4 gap-3 border-b border-gray-100 dark:border-gray-700">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Customer Code <span className="text-rose-500">*</span>
              </label>
              <input
                {...register('code')}
                disabled
                className={inputClass}
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Customer Name <span className="text-rose-500">*</span>
              </label>
              <input
                {...register('name')}
                disabled={fieldsDisabled}
                placeholder={screen.isInitial ? '—' : 'Enter customer name'}
                className={cn(inputClass, errors.name && 'border-rose-400')}
              />
              {errors.name && <p className="mt-0.5 text-[10px] text-rose-500">{errors.name.message}</p>}
            </div>
            <div className="flex items-end pb-0.5">
              <Controller name="isActive" control={control}
                render={({ field }) => (
                  <ToggleSwitch label="Active" checked={field.value} onChange={field.onChange} disabled={fieldsDisabled} />
                )} />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-0 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-5 sticky top-0 z-10">
            {TABS.map(tab => (
              <button key={tab} type="button" onClick={() => setActiveTab(tab)}
                className={cn(
                  'px-4 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap',
                  activeTab === tab
                    ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200',
                )}
              >{tab}</button>
            ))}
          </div>

          <div className="p-5 max-w-5xl">

            {/* ── GENERAL TAB ───────────────────────────────────────────── */}
            {activeTab === 'General' && (
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <SectionHeading>Identification</SectionHeading>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Short Name</label>
                      <input {...register('shortName')} disabled={fieldsDisabled}
                        placeholder={screen.isInitial ? '—' : 'Optional short name'} className={inputClass} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Tax Reg Number</label>
                      <input {...register('taxRegNumber')} disabled={fieldsDisabled}
                        placeholder={screen.isInitial ? '—' : 'VAT / GST / Tax number'} className={inputClass} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Collection Category</label>
                      <input {...register('collectionCategory')} disabled={fieldsDisabled}
                        placeholder={screen.isInitial ? '—' : 'e.g. Standard, Premium'} className={inputClass} />
                    </div>
                  </div>
                </div>

                <div>
                  <SectionHeading>GL Linkage</SectionHeading>
                  <AccountLookupField
                    label="Control Account"
                    value={controlAcct.id}
                    displayCode={controlAcct.code}
                    displayName={controlAcct.name}
                    onChange={(id, code, name) => setControlAcct({ id, code, name })}
                    disabled={fieldsDisabled}
                    isPostingAccount={true}
                    placeholder={screen.isInitial ? '—' : 'Click to select AR control account'}
                    hint="The AR control account this customer's balance is posted to"
                  />
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Remarks</label>
                    <textarea
                      {...register('remarks')}
                      disabled={fieldsDisabled}
                      rows={4}
                      placeholder={screen.isInitial ? '—' : 'Internal notes...'}
                      className={cn(
                        'w-full px-3 py-2 text-sm border rounded-md transition-colors resize-none',
                        'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
                        'border-gray-300 dark:border-gray-600',
                        'focus:outline-none focus:ring-2 focus:ring-brand-500',
                        'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
                      )}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ── FINANCIAL TAB ─────────────────────────────────────────── */}
            {activeTab === 'Financial' && (
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <SectionHeading>Currency</SectionHeading>
                  <div className="space-y-3 mb-5">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Default Currency</label>
                      <Controller name="currencyId" control={control}
                        render={({ field }) => (
                          <Combobox
                            options={currencies.map(c => ({ value: c.id, label: `${c.code} — ${c.name}` }))}
                            value={field.value ?? ''}
                            onChange={v => field.onChange(v || null)}
                            disabled={fieldsDisabled}
                            placeholder="— Use company default —"
                          />
                        )} />
                      <p className="mt-0.5 text-[10px] text-gray-400">Customer's billing currency. Leave blank to use company default.</p>
                    </div>
                  </div>
                  <SectionHeading>Credit Settings</SectionHeading>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Credit Limit</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                        <input
                          {...register('creditLimit')}
                          type="number" min="0" step="0.01"
                          disabled={fieldsDisabled}
                          className={cn(inputClass, 'pl-7 text-right', errors.creditLimit && 'border-rose-400')}
                        />
                      </div>
                      {errors.creditLimit && <p className="mt-0.5 text-[10px] text-rose-500">{errors.creditLimit.message}</p>}
                      <p className="mt-0.5 text-[10px] text-gray-400">Set to 0 for no credit limit</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Credit Period (days)</label>
                      <input
                        {...register('creditPeriod')}
                        type="number" min="0"
                        disabled={fieldsDisabled}
                        className={cn(inputClass, errors.creditPeriod && 'border-rose-400')}
                      />
                      {errors.creditPeriod && <p className="mt-0.5 text-[10px] text-rose-500">{errors.creditPeriod.message}</p>}
                    </div>
                  </div>
                </div>

                <div>
                  <SectionHeading>Account Options</SectionHeading>
                  <div className="space-y-1">
                    <Controller name="creditHoldStatus" control={control}
                      render={({ field }) => (
                        <ToggleSwitch label="Credit Hold" description="Blocks new transactions for this customer"
                          checked={field.value} onChange={field.onChange} disabled={fieldsDisabled} />
                      )} />
                    <Controller name="statementRequired" control={control}
                      render={({ field }) => (
                        <ToggleSwitch label="Statement Required" description="Generate monthly statements for this customer"
                          checked={field.value} onChange={field.onChange} disabled={fieldsDisabled} />
                      )} />
                    <Controller name="ageAnalysisEnabled" control={control}
                      render={({ field }) => (
                        <ToggleSwitch label="Age Analysis Enabled" description="Include in ageing reports"
                          checked={field.value} onChange={field.onChange} disabled={fieldsDisabled} />
                      )} />
                    <Controller name="interestApplicable" control={control}
                      render={({ field }) => (
                        <ToggleSwitch label="Interest Applicable" description="Charge interest on overdue balances"
                          checked={field.value} onChange={field.onChange} disabled={fieldsDisabled} />
                      )} />
                  </div>
                </div>
              </div>
            )}

            {/* ── ADDRESS TAB ───────────────────────────────────────────── */}
            {activeTab === 'Address' && (
              <div className="max-w-2xl space-y-3">
                <SectionHeading>Postal Address</SectionHeading>
                {(['address1', 'address2', 'address3'] as const).map((field, i) => (
                  <div key={field}>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {i === 0 ? 'Address Line 1' : i === 1 ? 'Address Line 2' : 'Address Line 3'}
                    </label>
                    <input {...register(field)} disabled={fieldsDisabled} className={inputClass} />
                  </div>
                ))}
                <div className="grid grid-cols-3 gap-3">
                  {(['city', 'stateProvince', 'postalCode'] as const).map(field => (
                    <div key={field}>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 capitalize">
                        {field === 'stateProvince' ? 'State / Province' : field === 'postalCode' ? 'Postal Code' : 'City'}
                      </label>
                      <input {...register(field)} disabled={fieldsDisabled} className={inputClass} />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Country</label>
                  <input {...register('country')} disabled={fieldsDisabled} className={inputClass} />
                </div>
              </div>
            )}

            {/* ── CONTACT TAB ───────────────────────────────────────────── */}
            {activeTab === 'Contact' && (
              <div className="max-w-2xl space-y-3">
                <SectionHeading>Contact Details</SectionHeading>
                {([
                  { key: 'contactPerson', label: 'Contact Person', type: 'text' },
                  { key: 'phone',         label: 'Phone',          type: 'tel' },
                  { key: 'mobile',        label: 'Mobile',         type: 'tel' },
                  { key: 'email',         label: 'Email',          type: 'email' },
                  { key: 'website',       label: 'Website',        type: 'url' },
                ] as const).map(({ key, label, type }) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{label}</label>
                    <input
                      {...register(key)}
                      type={type}
                      disabled={fieldsDisabled}
                      className={cn(inputClass, errors[key] && 'border-rose-400')}
                    />
                    {errors[key] && <p className="mt-0.5 text-[10px] text-rose-500">{(errors[key] as { message?: string })?.message}</p>}
                  </div>
                ))}
              </div>
            )}

            {/* ── AUDIT TAB ─────────────────────────────────────────────── */}
            {activeTab === 'Audit' && (
              <div className="max-w-lg">
                <SectionHeading>Audit Trail</SectionHeading>
                {record ? (
                  <div className="space-y-2 text-xs">
                    {[
                      { label: 'Created By',    value: record.createdByName ?? '—' },
                      { label: 'Created Date',  value: fmtDateTime(record.createdDate) },
                      { label: 'Modified By',   value: record.modifiedByName ?? '—' },
                      { label: 'Modified Date', value: fmtDateTime(record.modifiedDate) },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex gap-4 py-1 border-b border-gray-100 dark:border-gray-700">
                        <span className="w-32 text-gray-500 dark:text-gray-400 flex-shrink-0">{label}</span>
                        <span className="text-gray-700 dark:text-gray-300">{value}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">Audit information will appear after the record is saved.</p>
                )}
              </div>
            )}

          </div>
        </form>
      </div>

      {/* ── Modals ────────────────────────────────────────────────────────── */}
      <MasterSearchModal<CustomerListDto>
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        title="Customer Search"
        columns={SEARCH_COLUMNS}
        fetchData={fetchCustomers}
        onSelect={handleSearchSelect}
      />

      <ConfirmDialog isOpen={showNewConfirm !== null} variant="info"
        title="Create New Customer"
        message={<span>Customer <strong className="font-mono">{showNewConfirm}</strong> does not exist. Create a new record?</span>}
        confirmLabel="Yes, Create" cancelLabel="No"
        onConfirm={handleConfirmCreateNew} onCancel={() => setShowNewConfirm(null)}
      />

      <ConfirmDialog isOpen={showSaveConfirm} variant="save"
        title={!record ? 'Save New Customer' : 'Save Changes'}
        message={!record ? `Create new customer "${pendingSave?.code}"?` : `Save changes to customer "${record.code}"?`}
        confirmLabel="Yes, Save" cancelLabel="No" loading={saving}
        onConfirm={executeSave} onCancel={() => { setShowSaveConfirm(false); setPendingSave(null); }}
      />

      <ConfirmDialog isOpen={showDeleteConfirm} variant="delete"
        title="Delete Customer"
        message={<span>Permanently delete customer <strong>{record?.code} — {record?.name}</strong>? This cannot be undone.</span>}
        confirmLabel="Yes, Delete" cancelLabel="No" loading={saving}
        onConfirm={executeDelete} onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* Print preview */}
      {record && (
        <MasterReportPreview isOpen={printOpen} onClose={() => setPrintOpen(false)} title="Customer Master Report">
          <ReportHeader
            reportTitle="Customer Master Report"
            printedBy={user?.fullName ?? user?.username}
            printedAt={new Date()}
            meta={[{ label: 'Customer', value: `${record.code} — ${record.name}` }]}
          />
          <ReportSection title="General" fields={[
            { label: 'Code',            value: record.code },
            { label: 'Name',            value: record.name, wide: true },
            { label: 'Short Name',      value: record.shortName ?? '—' },
            { label: 'Status',          value: record.isActive ? 'Active' : 'Inactive' },
            { label: 'Control Account', value: record.controlAccountCode ?? '—' },
            { label: 'Tax Reg No',      value: record.taxRegNumber ?? '—' },
            { label: 'Collection Cat.', value: record.collectionCategory ?? '—' },
          ]} />
          <ReportSection title="Financial" fields={[
            { label: 'Credit Limit',        value: record.creditLimit.toFixed(2) },
            { label: 'Credit Period',       value: `${record.creditPeriod} days` },
            { label: 'Credit Hold',         value: record.creditHoldStatus ? 'YES' : 'No' },
            { label: 'Statement Required',  value: record.statementRequired ? 'Yes' : 'No' },
            { label: 'Age Analysis',        value: record.ageAnalysisEnabled ? 'Yes' : 'No' },
            { label: 'Interest Applicable', value: record.interestApplicable ? 'Yes' : 'No' },
          ]} />
          <ReportSection title="Address" fields={[
            { label: 'Address 1', value: record.address1 ?? '—', wide: true },
            { label: 'Address 2', value: record.address2 ?? '—', wide: true },
            { label: 'City',      value: record.city ?? '—' },
            { label: 'State',     value: record.stateProvince ?? '—' },
            { label: 'Postal',    value: record.postalCode ?? '—' },
            { label: 'Country',   value: record.country ?? '—' },
          ]} />
          <ReportSection title="Contact" fields={[
            { label: 'Contact Person', value: record.contactPerson ?? '—' },
            { label: 'Phone',          value: record.phone ?? '—' },
            { label: 'Mobile',         value: record.mobile ?? '—' },
            { label: 'Email',          value: record.email ?? '—' },
            { label: 'Website',        value: record.website ?? '—' },
          ]} />
          <ReportSection title="Audit" fields={[
            { label: 'Created By',   value: record.createdByName ?? record.createdBy ?? '—' },
            { label: 'Created Date', value: fmtDateTime(record.createdDate) },
            { label: 'Modified By',  value: record.modifiedByName ?? record.modifiedBy ?? '—' },
            { label: 'Modified Date',value: fmtDateTime(record.modifiedDate) },
          ]} />
          <ReportFooter />
        </MasterReportPreview>
      )}
    </div>
  );
};
